import { useState } from "react";
import { useCabinets, useSyncFromBajie, useRefreshById } from "../lib/api.js";
import { CabinetCard } from "./CabinetCard.js";
import { EventList } from "./EventList.js";
import { Stats } from "./Stats.js";

type StatusFilter = "all" | "online" | "offline";

export function Dashboard() {
  const { data, isLoading, error } = useCabinets();
  const all = data ?? [];
  const sync = useSyncFromBajie();
  const refreshById = useRefreshById();
  const [snInput, setSnInput] = useState("");
  const [status, setStatus] = useState<StatusFilter>("all");
  const [search, setSearch] = useState("");

  const cabinets = all.filter((c) => {
    if (status === "online" && !c.online) return false;
    if (status === "offline" && c.online) return false;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      const hay = `${c.id} ${c.shopId ?? ""} ${c.type ?? ""}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  const counts = {
    all: all.length,
    online: all.filter((c) => c.online).length,
    offline: all.filter((c) => !c.online).length,
  };

  return (
    <div className="mx-auto max-w-7xl px-6 py-8">
      <header className="mb-6 flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-zinc-500">
            Fleet overview
          </div>
          <h1 className="mt-1 text-3xl font-semibold tracking-tight brand-text">
            Every kiosk, every battery, live
          </h1>
        </div>
        <a
          href="https://boostedcharge.com"
          target="_blank"
          rel="noreferrer"
          className="text-xs text-zinc-500 hover:text-brand-orange transition"
        >
          boostedcharge.com ↗
        </a>
      </header>

      <div className="mb-6 flex flex-wrap items-center gap-2">
        <button
          onClick={() => sync.mutate()}
          disabled={sync.isPending}
          className="btn-primary"
        >
          {sync.isPending ? "Syncing…" : "Sync from Bajie"}
        </button>
        <span className="text-[11px] text-zinc-500">
          calls Bajie&apos;s Get Device List, upserts every cabinet
        </span>
        {sync.data && "count" in sync.data && (
          <span className="ml-2 text-[11px] text-emerald-300">
            ✓ {sync.data.count} found · {sync.data.upserted ?? 0} upserted
          </span>
        )}
        {sync.isError && (
          <span className="ml-2 text-[11px] text-rose-300">{(sync.error as Error).message}</span>
        )}

        <span className="mx-2 text-zinc-700">·</span>

        <input
          type="text"
          value={snInput}
          onChange={(e) => setSnInput(e.target.value)}
          placeholder="Cabinet SN (e.g. BJX01553)"
          className="rounded-md border border-zinc-800 bg-zinc-950/60 px-3 py-1.5 text-xs text-zinc-200 placeholder-zinc-600 focus:border-brand-purple/60 focus:outline-none focus:ring-1 focus:ring-brand-purple/40"
        />
        <button
          onClick={() => snInput.trim() && refreshById.mutate(snInput.trim())}
          disabled={refreshById.isPending || !snInput.trim()}
          className="btn-purple"
        >
          {refreshById.isPending ? "Adding…" : "Add by SN"}
        </button>
        <span className="text-[11px] text-zinc-500">
          targeted refresh — useful for new kiosks
        </span>
      </div>

      <Stats />

      <div className="mt-6 flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1 rounded-lg border border-zinc-800 bg-zinc-950/60 p-1">
          {(["all", "online", "offline"] as StatusFilter[]).map((s) => (
            <button
              key={s}
              onClick={() => setStatus(s)}
              className={
                "rounded-md px-3 py-1 text-xs font-medium transition " +
                (status === s
                  ? "bg-brand-purple/20 text-purple-100 ring-1 ring-brand-purple/40"
                  : "text-zinc-400 hover:text-zinc-200")
              }
            >
              {s} <span className={status === s ? "text-purple-200/70" : "text-zinc-500"}>· {counts[s]}</span>
            </button>
          ))}
        </div>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search SN / shop"
          className="rounded-md border border-zinc-800 bg-zinc-950/60 px-3 py-1.5 text-xs text-zinc-200 placeholder-zinc-600 focus:border-brand-purple/60 focus:outline-none focus:ring-1 focus:ring-brand-purple/40"
        />
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_22rem]">
        <section>
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-zinc-200">Fleet</h2>
            <span className="text-xs text-zinc-500">
              {isLoading
                ? "loading…"
                : `${cabinets.length} of ${all.length} cabinet${all.length === 1 ? "" : "s"}`}
            </span>
          </div>
          {error && (
            <div className="card p-6 text-sm text-rose-300">
              Failed to load: {(error as Error).message}
            </div>
          )}
          {!error && !isLoading && cabinets.length === 0 && (
            <div className="card p-10 text-center text-sm text-zinc-400">
              {all.length === 0
                ? "No cabinets registered yet. Click “Sync from Bajie” above to pull the fleet."
                : "No cabinets match the current filters."}
            </div>
          )}
          {cabinets.length > 0 && (
            <div className="card overflow-hidden">
              {/* column headers — keep widths in sync with CabinetCard's grid */}
              <div className="grid grid-cols-[auto_1fr_auto_8rem_auto] items-center gap-4 border-b border-zinc-800/60 bg-zinc-950/40 px-4 py-2 text-[10px] font-medium uppercase tracking-wider text-zinc-500">
                <span>Cabinet</span>
                <span>Shop · type</span>
                <span>Signal</span>
                <span>Slots</span>
                <span>Last seen</span>
              </div>
              {cabinets.map((c) => (
                <CabinetCard key={c.id} cabinet={c} />
              ))}
            </div>
          )}
        </section>

        <aside>
          <EventList />
        </aside>
      </div>
    </div>
  );
}
